// v16.44 — Forgot Password với 2 method: Email (gửi Resend link) hoặc
// SĐT Zalo (OTP ZNS → verify → nhận reset_token → set mật khẩu mới).
//
// Method Email: user nhập email → BE gửi link chứa token → user click link
//   sẽ mở /reset-password?token=X (page riêng).
//
// Method Zalo (multi-step trong page này):
//   1. User chọn tab "SĐT Zalo" → nhập phone → tap "Gửi mã".
//   2. BE gửi OTP qua ZNS → hiện input OTP + input mật khẩu mới.
//   3. User nhập OTP + mật khẩu mới → tap "Xác nhận" → verify + reset.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Envelope, ArrowLeft, PaperPlaneTilt, DeviceMobile, Lock, ShieldCheck,
} from "@phosphor-icons/react";
import { authApi, formatApiErrorDetail } from "../lib/authApi";
import Logo from "../components/common/Logo";
import BackButton from "../components/common/BackButton";

const METHODS = [
  { id: "email", label: "Email", icon: Envelope },
  { id: "zalo", label: "SĐT Zalo", icon: DeviceMobile },
];

// Zalo sub-steps
const ZALO_STEP_PHONE = "phone";
const ZALO_STEP_VERIFY = "verify";
const ZALO_STEP_DONE = "done";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [method, setMethod] = useState("email");

  // Email state
  const [email, setEmail] = useState("");
  const [busyEmail, setBusyEmail] = useState(false);
  const [sentEmail, setSentEmail] = useState(false);

  // Zalo state
  const [zaloStep, setZaloStep] = useState(ZALO_STEP_PHONE);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busyZalo, setBusyZalo] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (busyEmail) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error("Vui lòng nhập email hợp lệ");
      return;
    }
    setBusyEmail(true);
    try {
      await authApi.forgotPassword(trimmed);
      setSentEmail(true);
      toast.success("Đã gửi yêu cầu đặt lại mật khẩu");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Có lỗi xảy ra, vui lòng thử lại",
      );
    } finally {
      setBusyEmail(false);
    }
  };

  const submitZaloRequest = async (e) => {
    e.preventDefault();
    if (busyZalo) return;
    const p = phone.replace(/\D/g, "");
    if (p.length < 9) {
      toast.error("SĐT không hợp lệ");
      return;
    }
    setBusyZalo(true);
    try {
      const res = await authApi.forgotPasswordZaloRequestOtp(p);
      setZaloStep(ZALO_STEP_VERIFY);
      if (res?.sent === false) {
        toast.info("Nếu SĐT tồn tại, mã OTP đã được gửi qua Zalo.");
      } else {
        toast.success("Mã OTP đã gửi qua Zalo. Kiểm tra tin nhắn Zalo.");
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Không gửi được mã.",
      );
    } finally {
      setBusyZalo(false);
    }
  };

  const submitZaloVerify = async (e) => {
    e.preventDefault();
    if (busyZalo) return;
    if (otp.trim().length < 4) {
      toast.error("Nhập mã OTP.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Mật khẩu mới phải tối thiểu 8 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Mật khẩu xác nhận không khớp.");
      return;
    }
    setBusyZalo(true);
    try {
      const p = phone.replace(/\D/g, "");
      const verifyRes = await authApi.forgotPasswordZaloVerifyOtp(p, otp.trim());
      const token = verifyRes?.reset_token;
      if (!token) throw new Error("Thiếu reset_token từ server.");
      setResetToken(token);
      await authApi.resetPassword(token, newPassword);
      setZaloStep(ZALO_STEP_DONE);
      toast.success("Đã đặt lại mật khẩu thành công!");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Xác nhận OTP thất bại.",
      );
    } finally {
      setBusyZalo(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B1220] via-[#0F1A2E] to-[#10162B] px-4 py-10"
      data-testid="forgot-password-page"
    >
      <BackButton testId="forgot-back" />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo size={64} />
        </div>
        <div className="bg-gradient-to-br from-[#0F1A2E] to-[#0B1220] border border-white/10 rounded-3xl shadow-2xl p-6 sm:p-8">
          <div className="mb-5">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-[12px] text-white/55 hover:text-white inline-flex items-center gap-1.5 mb-3"
              data-testid="forgot-back-btn"
            >
              <ArrowLeft size={12} weight="bold" />
              Quay lại đăng nhập
            </button>
            <h1 className="text-2xl font-black text-white tracking-tight">
              Quên mật khẩu?
            </h1>
            <p className="text-[13px] text-white/65 mt-1.5 leading-relaxed">
              Chọn phương thức xác thực để đặt lại mật khẩu.
            </p>
          </div>

          {/* Method toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-[#0F1623] border border-white/10 mb-5">
            {METHODS.map((m) => {
              const Icon = m.icon;
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={`h-10 rounded-xl inline-flex items-center justify-center gap-2 text-[12.5px] font-black transition ${
                    active
                      ? "bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white shadow-[0_4px_14px_rgba(255,138,0,0.4)]"
                      : "text-white/60 hover:text-white/85"
                  }`}
                  data-testid={`forgot-method-${m.id}`}
                >
                  <Icon size={14} weight="bold" />
                  {m.label}
                </button>
              );
            })}
          </div>

          {method === "email" ? (
            sentEmail ? (
              <div
                className="rounded-2xl border border-emerald-400/30 bg-emerald-500/8 p-4 text-[13px] text-emerald-100 leading-relaxed"
                data-testid="forgot-email-success"
              >
                <div className="font-black mb-1.5">✅ Đã gửi yêu cầu</div>
                <p>
                  Nếu email <b>{email}</b> tồn tại trong hệ thống, bạn sẽ nhận
                  được link đặt lại mật khẩu trong ít phút. Link có hiệu lực
                  trong <b>1 giờ</b>.
                </p>
                <Link
                  to="/login"
                  className="mt-4 block text-center px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062014] font-black text-[12.5px]"
                  data-testid="forgot-back-login-btn"
                >
                  Về trang đăng nhập
                </Link>
              </div>
            ) : (
              <form onSubmit={submitEmail} className="space-y-4">
                <label className="block">
                  <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                    Email
                  </span>
                  <div className="relative">
                    <Envelope
                      size={16}
                      weight="bold"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                    />
                    <input
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ban@gmail.com"
                      className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[14px] outline-none focus:border-[#FF8A00]/60"
                      data-testid="forgot-email-input"
                      disabled={busyEmail}
                    />
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={busyEmail}
                  className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="forgot-email-submit"
                >
                  {busyEmail ? "Đang gửi…" : (
                    <>
                      <PaperPlaneTilt size={14} weight="bold" />
                      Gửi link qua Email
                    </>
                  )}
                </button>
              </form>
            )
          ) : zaloStep === ZALO_STEP_PHONE ? (
            <form onSubmit={submitZaloRequest} className="space-y-4">
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Số điện thoại (đăng ký Zalo)
                </span>
                <div className="relative">
                  <DeviceMobile
                    size={16}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0901234567"
                    className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[14px] outline-none focus:border-[#FF8A00]/60"
                    data-testid="forgot-zalo-phone-input"
                    disabled={busyZalo}
                  />
                </div>
                <p className="text-[11px] text-white/40 mt-1.5">
                  SĐT phải trùng số đã đăng ký tài khoản.
                </p>
              </label>
              <button
                type="submit"
                disabled={busyZalo}
                className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="forgot-zalo-request-otp"
              >
                {busyZalo ? "Đang gửi…" : (
                  <>
                    <PaperPlaneTilt size={14} weight="bold" />
                    Gửi mã OTP qua Zalo
                  </>
                )}
              </button>
            </form>
          ) : zaloStep === ZALO_STEP_VERIFY ? (
            <form onSubmit={submitZaloVerify} className="space-y-4">
              <div className="rounded-xl bg-emerald-500/8 border border-emerald-400/20 p-3 text-[12px] text-emerald-100">
                Đã gửi mã đến Zalo của <b>{phone}</b>. Mở app Zalo để lấy mã.
              </div>

              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Mã OTP (6 số)
                </span>
                <div className="relative">
                  <ShieldCheck
                    size={16}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[16px] tracking-widest outline-none focus:border-[#FF8A00]/60"
                    data-testid="forgot-zalo-otp-input"
                    disabled={busyZalo}
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Mật khẩu mới
                </span>
                <div className="relative">
                  <Lock
                    size={16}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Tối thiểu 8 ký tự"
                    className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[14px] outline-none focus:border-[#FF8A00]/60"
                    data-testid="forgot-zalo-newpass"
                    disabled={busyZalo}
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Nhập lại mật khẩu
                </span>
                <div className="relative">
                  <Lock
                    size={16}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu mới"
                    className="w-full h-12 pl-9 pr-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[14px] outline-none focus:border-[#FF8A00]/60"
                    data-testid="forgot-zalo-confirmpass"
                    disabled={busyZalo}
                  />
                </div>
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setZaloStep(ZALO_STEP_PHONE); setOtp(""); setNewPassword(""); setConfirmPassword(""); }}
                  disabled={busyZalo}
                  className="h-12 px-4 rounded-2xl bg-white/8 hover:bg-white/14 text-white/85 text-[12.5px] font-black transition"
                  data-testid="forgot-zalo-back"
                >
                  Đổi SĐT
                </button>
                <button
                  type="submit"
                  disabled={busyZalo}
                  className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="forgot-zalo-verify-submit"
                >
                  {busyZalo ? "Đang xác nhận…" : (
                    <>
                      <ShieldCheck size={14} weight="bold" />
                      Xác nhận & đặt mật khẩu
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div
              className="rounded-2xl border border-emerald-400/30 bg-emerald-500/8 p-4 text-[13px] text-emerald-100 leading-relaxed text-center"
              data-testid="forgot-zalo-success"
            >
              <div className="font-black mb-1.5 text-lg">✅ Đặt mật khẩu thành công</div>
              <p>Bạn có thể đăng nhập bằng mật khẩu mới ngay bây giờ.</p>
              <Link
                to="/login"
                className="mt-4 inline-block px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062014] font-black text-[12.5px]"
                data-testid="forgot-zalo-goto-login"
              >
                Đăng nhập
              </Link>
            </div>
          )}

          {(method === "email" && !sentEmail) || (method === "zalo" && zaloStep === ZALO_STEP_PHONE) ? (
            <div className="text-center pt-4">
              <Link
                to="/login"
                className="text-[12px] text-white/55 hover:text-white"
                data-testid="forgot-login-link"
              >
                Nhớ ra mật khẩu? <span className="text-white">Đăng nhập</span>
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
